import React from 'react';
import { BRAND } from '../../lib/brand';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  Divider,
  IconButton
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';

const PublicFooter: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'Product',
      links: [
        { name: 'Free Tools', path: '/tools' },
        { name: 'Compare', path: '/compare' },
        { name: 'SEC Incident Tracker', path: '/incidents' },
        { name: 'Threat Tracker', path: '/threats' },
        { name: 'Features', path: '/#features' },
        { name: 'Pricing', path: '/#pricing' },
        { name: 'Blog', path: '/blog' },
        { name: 'FAQ', path: '/faq' }
      ]
    },
    {
      title: 'Legal',
      links: [
        { name: 'Privacy Policy', path: '/privacy' },
        { name: 'Terms of Service', path: '/terms' }
      ]
    }
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'background.default',
        py: 7,
        borderTop: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Brand and Description */}
          <Grid item xs={12} md={4}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 2
              }}
            >
              {BRAND.productName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 340, lineHeight: 1.6 }}>
              Real security policies for teams without a compliance department. Generate, map, and
              maintain what auditors actually want to see.
            </Typography>
            <Box sx={{ mb: 2, display: 'flex', gap: 0.5 }}>
              <IconButton
                href={BRAND.social.twitter}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
                sx={{ color: 'text.secondary' }}
              >
                <TwitterIcon fontSize="small" />
              </IconButton>
              <IconButton
                href={BRAND.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                sx={{ color: 'text.secondary' }}
              >
                <LinkedInIcon fontSize="small" />
              </IconButton>
              <IconButton
                href={BRAND.social.github}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                sx={{ color: 'text.secondary' }}
              >
                <GitHubIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>

          {/* Footer Sections */}
          {footerSections.map((section) => (
            <Grid item xs={6} sm={3} md={2} key={section.title}>
              <Typography
                variant="subtitle2"
                color="text.primary"
                gutterBottom
                sx={{ fontWeight: 600, mb: 1.5 }}
              >
                {section.title}
              </Typography>
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {section.links.map((link) => (
                  <Box component="li" key={link.name} sx={{ mb: 1.25 }}>
                    <Link
                      component={RouterLink}
                      to={link.path}
                      color="text.secondary"
                      variant="body2"
                      sx={{
                        textDecoration: 'none',
                        '&:hover': {
                          color: 'secondary.main'
                        }
                      }}
                    >
                      {link.name}
                    </Link>
                  </Box>
                ))}
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Bottom Footer */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Typography variant="body2" color="text.secondary">
            © {currentYear} {BRAND.productName}. All rights reserved.
          </Typography>
          <Box sx={{
            display: 'flex',
            gap: 3,
            '& > a': {
              color: 'text.secondary',
              textDecoration: 'none',
              fontSize: '0.875rem',
              '&:hover': {
                color: 'secondary.main'
              }
            }
          }}>
            <Link component={RouterLink} to="/privacy">Privacy</Link>
            <Link component={RouterLink} to="/terms">Terms</Link>
            <Link href={`mailto:${BRAND.supportEmail}`}>Contact</Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PublicFooter;
